import {useEffect, useState} from 'react';
import {tr} from '../i18n/idioma';

// Descansa pantalla: tras unos minutos sin actividad aparece un barco que
// atraca, una grua lo llena de contenedores y zarpa. Cualquier movimiento del
// mouse, tecla o toque lo quita. Todo es dibujo propio: no pide nada a internet.
export const MINUTOS_PARA_DESCANSO = 8;
const EVENTOS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

// Linea de tiempo de cada ciclo (segundos).
const COLUMNAS = 6;
const FILAS = 3;
const CONTENEDORES = COLUMNAS * FILAS;
const LLEGADA = 4;
const POR_CONTENEDOR = 0.8;
const FIN_DE_CARGA = LLEGADA + CONTENEDORES * POR_CONTENEDOR;
const ZARPE = 11;
const CICLO = FIN_DE_CARGA + ZARPE + 2;

// Escena (coordenadas del dibujo).
const ANCHO = 1600;
const ALTO = 900;
const MAR = 640;
const CUBIERTA = 600; // altura de la cubierta del barco atracado
const MUELLE_X = 260;
const ATRACADO_X = 290;
const BRAZO_Y = 300;
const RECOGIDA_X = 150;
const ANCHO_CONT = 70;
const ALTO_CONT = 30;
const COLORES = ['#16775f', '#d8ad5e', '#9a722c', '#3b6fb6', '#b3261e', '#6f7c7f', '#2d5f56', '#e0c48a'];
const ESTRELLAS = Array.from({length: 60}, (_, i) => ({
  x: (i * 263) % ANCHO,
  y: (i * 97) % 380,
  r: 0.8 + (i % 3) * 0.6,
}));

const suave = (p: number) => p * p * (3 - 2 * p);
const entre = (t: number, a: number, b: number) => Math.max(0, Math.min(1, (t - a) / (b - a)));
const hueco = (i: number) => ({
  x: 40 + (i % COLUMNAS) * (ANCHO_CONT + 6),
  y: -ALTO_CONT * (Math.floor(i / COLUMNAS) + 1),
});

export function DescansaPantalla({activo}: {activo: boolean}) {
  const [descansando, setDescansando] = useState(false);

  useEffect(() => {
    if (!activo) {
      setDescansando(false);
      return;
    }
    let temporizador = window.setTimeout(() => setDescansando(true), MINUTOS_PARA_DESCANSO * 60_000);
    const actividad = () => {
      setDescansando(false);
      window.clearTimeout(temporizador);
      temporizador = window.setTimeout(() => setDescansando(true), MINUTOS_PARA_DESCANSO * 60_000);
    };
    for (const e of EVENTOS) window.addEventListener(e, actividad, {passive: true, capture: true});
    return () => {
      window.clearTimeout(temporizador);
      for (const e of EVENTOS) window.removeEventListener(e, actividad, {capture: true});
    };
  }, [activo]);

  if (!descansando) return null;
  return <Escena />;
}

function Escena() {
  const quieto = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [t, setT] = useState(quieto ? FIN_DE_CARGA : 0);
  const [ahora, setAhora] = useState(() => new Date());

  useEffect(() => {
    if (quieto) return;
    let pedido = 0;
    const inicio = performance.now();
    const cuadro = (momento: number) => {
      setT(((momento - inicio) / 1000) % CICLO);
      pedido = requestAnimationFrame(cuadro);
    };
    pedido = requestAnimationFrame(cuadro);
    return () => cancelAnimationFrame(pedido);
  }, [quieto]);

  useEffect(() => {
    const reloj = window.setInterval(() => setAhora(new Date()), 15_000);
    return () => window.clearInterval(reloj);
  }, []);

  // Posicion del barco: llega, espera la carga y zarpa hacia la derecha.
  const barcoX =
    t < LLEGADA
      ? -720 + (ATRACADO_X + 720) * suave(entre(t, 0, LLEGADA))
      : t < FIN_DE_CARGA
        ? ATRACADO_X
        : ATRACADO_X + (ANCHO + 60 - ATRACADO_X) * entre(t, FIN_DE_CARGA, FIN_DE_CARGA + ZARPE) ** 2;
  const vaiven = Math.sin(t * 1.6) * 3;
  const cabeceo = Math.sin(t * 1.1) * 0.6;
  const cargados = Math.max(0, Math.min(CONTENEDORES, Math.floor((t - LLEGADA) / POR_CONTENEDOR)));

  // El contenedor que la grua esta moviendo: viaja por el brazo y baja a su hueco.
  let enGrua: {x: number; y: number; color: string} | null = null;
  let carroX = RECOGIDA_X;
  if (t >= LLEGADA && t < FIN_DE_CARGA) {
    const i = Math.floor((t - LLEGADA) / POR_CONTENEDOR);
    const p = ((t - LLEGADA) % POR_CONTENEDOR) / POR_CONTENEDOR;
    const destino = hueco(i);
    const destinoX = ATRACADO_X + destino.x;
    const destinoY = CUBIERTA + destino.y;
    const x = p < 0.5 ? RECOGIDA_X + (destinoX - RECOGIDA_X) * suave(p / 0.5) : destinoX;
    const y = p < 0.5 ? 380 : 380 + (destinoY - 380) * suave((p - 0.5) / 0.5);
    enGrua = {x, y, color: COLORES[i % COLORES.length]!};
    carroX = x + ANCHO_CONT / 2;
  }

  const hora = new Intl.DateTimeFormat(undefined, {hour: '2-digit', minute: '2-digit'}).format(ahora);
  const olas = (desfase: number, alto: number) => {
    const d = ((t * 40 + desfase) % 160) - 160;
    let ruta = `M ${d} ${MAR + alto}`;
    for (let x = d; x < ANCHO + 160; x += 80) ruta += ` q 40 -10 80 0`;
    return `${ruta} V ${ALTO} H ${d} Z`;
  };

  return (
    <div
      className="fixed inset-0 z-[100] cursor-none bg-[#0b2a26]"
      role="img"
      aria-label={tr(
        'Descansa pantalla: un barco se llena de contenedores y zarpa',
        'Screensaver: a ship is loaded with containers and sails away',
      )}
      data-descansa-pantalla
    >
      <svg viewBox={`0 0 ${ANCHO} ${ALTO}`} preserveAspectRatio="xMidYMid slice" className="h-full w-full">
        <defs>
          <linearGradient id="dp-cielo" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#081f1c" />
            <stop offset="1" stopColor="#1d4b43" />
          </linearGradient>
          <linearGradient id="dp-mar" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#0f5a4e" />
            <stop offset="1" stopColor="#052420" />
          </linearGradient>
        </defs>
        <rect width={ANCHO} height={MAR + 10} fill="url(#dp-cielo)" />
        {ESTRELLAS.map((e, i) => (
          <circle
            key={i}
            cx={e.x}
            cy={e.y}
            r={e.r}
            fill="#f3e8c8"
            opacity={0.35 + 0.35 * Math.abs(Math.sin(t * 0.8 + i))}
          />
        ))}
        <circle cx={1360} cy={150} r={70} fill="#f3e8c8" opacity={0.08} />
        <circle cx={1360} cy={150} r={44} fill="#f3e8c8" opacity={0.92} />

        {/* Barco */}
        <g transform={`translate(${barcoX} ${CUBIERTA + vaiven}) rotate(${cabeceo} 310 30)`}>
          {Array.from({length: 3}, (_, i) => {
            const fase = (t * 0.6 + i / 3) % 1;
            return (
              <circle
                key={i}
                cx={548 + fase * 40}
                cy={-150 - fase * 90}
                r={10 + fase * 18}
                fill="#cfd8d6"
                opacity={0.45 * (1 - fase)}
              />
            );
          })}
          <rect x={530} y={-140} width={34} height={50} fill="#b3261e" />
          <rect x={500} y={-95} width={90} height={95} fill="#e8ece9" />
          {[0, 1, 2].map(f => (
            <rect key={f} x={510 + f * 26} y={-82} width={16} height={10} fill="#1d4b43" />
          ))}
          <path d="M -10 0 L 640 0 L 600 78 L 30 78 Z" fill="#173b35" />
          <path d="M -10 0 L 640 0 L 634 12 L -4 12 Z" fill="#b3261e" />
          <text x={250} y={30} fill="#e0c48a" fontSize={18} fontWeight={700} fontFamily="sans-serif">
            CNC · SUN VALLEY
          </text>
          {Array.from({length: cargados}, (_, i) => {
            const h = hueco(i);
            return <Contenedor key={i} x={h.x} y={h.y} color={COLORES[i % COLORES.length]!} />;
          })}
        </g>

        {/* Muelle y grua por delante: el barco sale de detras al llegar */}
        <rect x={0} y={MAR - 40} width={MUELLE_X} height={80} fill="#3d4a47" />
        <rect x={0} y={MAR - 44} width={MUELLE_X} height={6} fill="#d8ad5e" />
        <rect x={60} y={MAR - 76} width={ANCHO_CONT} height={ALTO_CONT} rx={2} fill="#6f7c7f" />
        <g stroke="#e0c48a" strokeWidth={6} fill="none">
          <path d={`M 90 ${MAR - 40} L 110 ${BRAZO_Y} L 150 ${BRAZO_Y} L 170 ${MAR - 40}`} />
          <path d={`M 100 ${BRAZO_Y + 80} L 160 ${BRAZO_Y + 80} M 95 ${BRAZO_Y + 180} L 165 ${BRAZO_Y + 180}`} />
          <path d={`M 40 ${BRAZO_Y} L 900 ${BRAZO_Y}`} strokeWidth={10} />
          <path
            d={`M 130 ${BRAZO_Y} L 130 ${BRAZO_Y - 70} L 900 ${BRAZO_Y - 4} M 130 ${BRAZO_Y - 70} L 40 ${BRAZO_Y}`}
            strokeWidth={3}
          />
        </g>
        <rect x={carroX - 18} y={BRAZO_Y - 6} width={36} height={16} rx={3} fill="#b3261e" />
        {enGrua ? (
          <>
            <line x1={carroX} y1={BRAZO_Y + 10} x2={carroX} y2={enGrua.y} stroke="#cfd8d6" strokeWidth={2} />
            <Contenedor x={enGrua.x} y={enGrua.y} color={enGrua.color} />
          </>
        ) : (
          <line x1={carroX} y1={BRAZO_Y + 10} x2={carroX} y2={BRAZO_Y + 60} stroke="#cfd8d6" strokeWidth={2} />
        )}

        {/* Mar por delante del casco */}
        <path d={olas(0, 0)} fill="url(#dp-mar)" opacity={0.95} />
        <path d={olas(60, 18)} fill="#0a3b34" opacity={0.8} />
        <path d={olas(110, 40)} fill="#052420" opacity={0.9} />
      </svg>

      <div className="pointer-events-none absolute top-8 left-10 text-[#f3e8c8]">
        <div className="text-6xl font-light tabular-nums">{hora}</div>
        <div className="mt-2 text-lg opacity-80">{tr('CNC Inventario', 'CNC Inventory')}</div>
        <div className="mt-1 text-sm opacity-70">
          {tr(
            `Contenedores cargados: ${cargados} de ${CONTENEDORES}`,
            `Containers loaded: ${cargados} of ${CONTENEDORES}`,
          )}
        </div>
      </div>
      <div className="pointer-events-none absolute right-0 bottom-6 left-0 text-center text-sm text-[#f3e8c8] opacity-70">
        {tr('Mueve el mouse o toca una tecla para volver', 'Move the mouse or press a key to come back')}
      </div>
    </div>
  );
}

function Contenedor({x, y, color}: {x: number; y: number; color: string}) {
  return (
    <g>
      <rect x={x} y={y} width={ANCHO_CONT} height={ALTO_CONT} rx={2} fill={color} stroke="#0b2a26" strokeWidth={1.5} />
      {[1, 2, 3, 4, 5].map(k => (
        <line
          key={k}
          x1={x + (ANCHO_CONT / 6) * k}
          y1={y + 4}
          x2={x + (ANCHO_CONT / 6) * k}
          y2={y + ALTO_CONT - 4}
          stroke="#000"
          strokeOpacity={0.18}
        />
      ))}
    </g>
  );
}
